// /my-trips on the website: trips are managed in the Sotravel app, so this
// page points people to the stores. Also the fallback page behind the
// app-redirect links (LinkToApp passes `beforeTitle`). An account page
// (pages/account/kit.js). In the app the menu opens the Bookings tab instead.
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import { CalendarCheck, Sparkles, Users } from "lucide-react";
import useIsAndroid from "../customHooks/useIsAndroid";
import { Alert } from "../pages/manage/ui";
import { AccountPage, SettingsGroup, SettingsRow } from "../pages/account/kit";

const APP_STORE_URL = "https://apps.apple.com/sg/app/sotravel/id6447177449";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.sotravel.sotravel";

export const MyTrips = ({ beforeTitle }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isAndroid = useIsAndroid();

  useEffect(() => {
    const query = queryString.parse(location.search);

    if (query.redirect === "true") {
      window.open(isAndroid ? PLAY_STORE_URL : APP_STORE_URL, "_blank");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AccountPage
      title={t("account_menu_my_trips", { defaultValue: "My trips" })}
      description={t("app_description", {
        defaultValue: "Manage your booking, meet your travel group, and unlock a world of possibilities - all in one elegant app.",
      })}
      back={{ fallback: "/all-trips" }}
    >
      {beforeTitle && (
        <Alert variant="info" className="tw-mb-5">
          {beforeTitle}
        </Alert>
      )}
      <DownloadApp />
    </AccountPage>
  );
};

const StoreButton = ({ href, label, icon }) => {
  const { t } = useTranslation();
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="tw-flex tw-h-14 tw-shrink-0 tw-items-center sm:tw-flex-1 tw-justify-center tw-gap-3 tw-rounded-[var(--r-xl)] tw-bg-[#001624] tw-px-5 tw-text-white tw-transition-transform active:tw-scale-[0.98]"
    >
      <svg className="tw-size-7 tw-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d={icon} />
      </svg>
      <span className="tw-text-left tw-leading-tight">
        <span className="tw-block tw-text-[11px] tw-text-white/70">{t("download_from")}</span>
        <span className="tw-block tw-text-[17px] tw-font-semibold">{label}</span>
      </span>
    </a>
  );
};

const APPLE_ICON =
  "M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z";
const PLAY_ICON =
  "M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z";

export const DownloadApp = () => {
  const { t } = useTranslation();

  return (
    <>
      <div className="tw-flex tw-flex-col tw-gap-2.5 sm:tw-flex-row">
        <StoreButton href={APP_STORE_URL} label={t("app_store", { defaultValue: "App Store" })} icon={APPLE_ICON} />
        <StoreButton href={PLAY_STORE_URL} label={t("google_play", { defaultValue: "Google Play" })} icon={PLAY_ICON} />
      </div>

      <p className="tw-mb-2 tw-mt-8 tw-px-1 tw-text-sm tw-font-medium tw-text-muted-foreground">{t("account_why_app", { defaultValue: "Why get the app" })}</p>
      <SettingsGroup>
        <SettingsRow
          icon={CalendarCheck}
          label={t("seamless_booking_title", { defaultValue: "Seamless booking" })}
          description={t("seamless_booking_desc", { defaultValue: "Manage your travel plans with ease and convenience" })}
        />
        <SettingsRow
          icon={Users}
          label={t("connect_share_title", { defaultValue: "Connect & share" })}
          description={t("connect_share_desc", { defaultValue: "Meet fellow travelers and share your experiences" })}
        />
        <SettingsRow
          icon={Sparkles}
          label={t("exclusive_features_title", { defaultValue: "Exclusive features" })}
          description={t("exclusive_features_desc", { defaultValue: "Access special offers and unique travel content" })}
        />
      </SettingsGroup>
    </>
  );
};
